import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

/**
 * Supabase client used **only for Realtime subscriptions** on gallery pages.
 *
 * Reads + writes still go through Express via `apiFetch` — the backend is
 * the only path that holds the service-role key, and authorization checks
 * (`requireAuth`, `requireRole`) live there. This client connects to the
 * Realtime WebSocket using the project's anon key; the JWT issued by
 * `/api/auth/login` is fed in via `setRealtimeAuth(jwt)` so RLS policies
 * see the connection as `authenticated`.
 *
 * The JWT must be signed with the Supabase project's JWT secret (server
 * `auth.ts` is configured to use SUPABASE_JWT_SECRET when set), and must
 * carry `role: 'authenticated'` and `aud: 'authenticated'` claims —
 * Express's `issueToken` adds both unconditionally.
 */
let client: SupabaseClient<Database> | null = null;

function tryCreate(): SupabaseClient<Database> | null {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) return null;
  return createClient<Database>(url, anonKey, {
    // We don't use Supabase Auth — Express owns the session. Disable the
    // session-storage shenanigans so this client is purely a Realtime
    // transport.
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
  });
}

/**
 * Returns the singleton client, or `null` when Realtime isn't configured
 * (e.g. dev env without the env vars set). Callers should treat null as
 * "realtime disabled, fall back to React Query stale-while-refetch".
 */
export function getSupabase(): SupabaseClient<Database> | null {
  if (client === null) {
    client = tryCreate();
  }
  return client;
}

/**
 * Push the user's Express JWT into Realtime. Called once after login (and
 * after any token refresh) so the channel sees `authenticated` role for RLS.
 */
export function setRealtimeAuth(jwt: string | null): void {
  const sb = getSupabase();
  if (!sb) return;
  sb.realtime.setAuth(jwt);
}
